import {
  Card,
  CollapsibleCard,
  Divider,
  FormField,
  Heading,
  InputWidth,
  ReadOnlyField,
  RecordContent,
  RecordFooter,
  useTitleChange,
} from '@msaf/core-react'
import { useEffect, useState } from 'react'
import { TOOLTIP } from '../../../../../constants/tooltips'
import { FormFieldsView } from '../../../../../types/form-types'
import WellDetailsIdentifiersView from '../../../../components/well-forms/details/well-details-identifiers-view'
import WellDetailsInstallationConsentView from '../../../../components/well-forms/details/well-details-installation-consent-view'
import WellDetailsOwnerCardView from '../../../../components/well-forms/details/well-details-owner-card-view'
import NotesWrapperView from '../../../../components/well-forms/notes/notes-wrapper-view'
import AddressField from '../../../../components/wells/address-field'
import LocationFieldView from '../../../../components/wells/location-field-view'
import WellViewButtons from '../../../../components/wells/well-view-buttons'
import { useGetWellData } from '../../../../hooks/use-api'
import useUser from '../../../../hooks/use-user'
import { useWellId } from '../../../../hooks/use-well-id'
import { isCouncilUserOrAdmin, isDriller } from '../../../../services/user-utils'

export default function ViewWellDetails() {
  useTitleChange('View Well | Details')
  const id = useWellId()
  const [fields, setFields] = useState<Partial<FormFieldsView>>({})
  const url = `wells/${id}/details/view`
  const { user } = useUser()

  const { isLoading, isSuccess, data } = useGetWellData(url)

  useEffect(() => {
    if (isSuccess && data?.state) {
      setFields(data.state)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, data?.state])

  const getAboveMeasuringPointDisplayValue = () => {
    if (fields.waterLevelAboveMeasuringPoint === false) return 'Below measuring point'
    if (!fields.waterLevelAboveMeasuringPoint) return ''
    if (fields.waterLevelAboveMeasuringPoint) return 'Above measuring point'
  }

  const hasActualCoordinates = [
    fields.actualEasting,
    fields.actualNorthing,
    fields.actualLatitude,
    fields.actualLongitude,
  ].some((value) => !!value)

  const hasProposedCoordinates = [
    fields.proposedEasting,
    fields.proposedNorthing,
    fields.proposedLatitude,
    fields.proposedLongitude,
  ].some((value) => !!value)

  return (
    <>
      <RecordContent>
        <Heading level={2}>Details</Heading>
        <WellDetailsIdentifiersView isLoading={isLoading} fields={fields} />
        <Divider />
        <Heading isSkeleton={isLoading} level={3}>
          Location
        </Heading>
        {hasProposedCoordinates ? (
          <CollapsibleCard
            heading='Proposed'
            shouldDisplayCard={!hasActualCoordinates && hasProposedCoordinates}
            expandCard={{ text: 'Details', icon: 'chevron-down' }}
            collapseCard={{ text: 'Hide', icon: 'chevron-up' }}
          >
            <LocationFieldView
              easting={{
                labelId: 'proposedEasting',
                value: `${fields.proposedEasting ?? ''}`,
              }}
              northing={{
                labelId: 'proposedNorthing',
                value: `${fields.proposedNorthing ?? ''}`,
              }}
              latitude={{
                labelId: 'proposedLatitude',
                value: `${fields.proposedLatitude ?? ''}`,
              }}
              longitude={{
                labelId: 'proposedLongitude',
                value: `${fields.proposedLongitude ?? ''}`,
              }}
            />
          </CollapsibleCard>
        ) : (
          <Card>
            <Heading isSkeleton={isLoading} level={4}>
              Proposed
            </Heading>
            <p>There are no proposed coordinates to display.</p>
          </Card>
        )}
        {hasActualCoordinates ? (
          <CollapsibleCard
            heading='Confirmed'
            shouldDisplayCard={hasActualCoordinates}
            expandCard={{ text: 'Details', icon: 'chevron-down' }}
            collapseCard={{ text: 'Hide', icon: 'chevron-up' }}
          >
            <LocationFieldView
              easting={{
                labelId: 'actualEasting',
                value: `${fields.actualEasting ?? ''}`,
              }}
              northing={{
                labelId: 'actualNorthing',
                value: `${fields.actualNorthing ?? ''}`,
              }}
              latitude={{
                labelId: 'actualLatitude',
                value: `${fields.actualLatitude ?? ''}`,
              }}
              longitude={{
                labelId: 'actualLongitude',
                value: `${fields.actualLongitude ?? ''}`,
              }}
              locationAccuracy={fields.locationAccuracy}
              distanceBetweenProposedAndActualWell={{
                value: fields.distanceBetweenProposedAndActualWell,
                canRender: true,
              }}
            />
          </CollapsibleCard>
        ) : (
          <Card>
            <Heading isSkeleton={isLoading} level={4}>
              Confirmed
            </Heading>
            <p>There are no confirmed coordinates to display.</p>
          </Card>
        )}
        <AddressField
          isSkeleton={isLoading}
          addressFields={{
            addressLine1: `${fields['wellAddressLine1'] ?? ''}`,
            addressLine2: `${fields['wellAddressLine2'] ?? ''}`,
            addressLine3: `${fields['wellAddressLine3'] ?? ''}`,
            addressLine4: `${fields['wellAddressLine4'] ?? ''}`,
            addressLine5: `${fields['wellAddressLine4'] ?? ''}`,
            addressPostcode: `${fields['wellAddressPostcode'] ?? ''}`,
            isManualEntry: !!fields['isWellAddressEnteredManually'],
          }}
          labelledBy='wellAddress'
          isReadOnly={true}
          setAddressFields={(_addressFields) => {
            /** Do nothing for read-only address fields */
          }}
        />
        <FormField isSkeleton={isLoading} labelId='wellLocality' label='Locality'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='wellLocality' value={`${fields.wellLocality ?? ''}`} />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='region' label='Region'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='region' value={`${fields.councilName ?? ''}`} />
        </FormField>
        <FormField
          toolTipMessage={TOOLTIP.locationDescription}
          isSkeleton={isLoading}
          labelId='locationDescription'
          label='Location description'
        >
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='locationDescription'
            value={`${fields.locationDescription ?? ''}`}
          />
        </FormField>
        <WellDetailsOwnerCardView isLoading={isLoading} fields={fields} />
        <Divider />
        <WellDetailsInstallationConsentView isLoading={isLoading} fields={fields} />
        <Divider />
        <Heading isSkeleton={isLoading} level={3}>
          Drilling
        </Heading>
        <FormField
          toolTipMessage={TOOLTIP.proposedMaxDepthDrilled}
          isSkeleton={isLoading}
          labelId='proposedDepth'
          label='Proposed depth'
        >
          <ReadOnlyField
            fieldSize={InputWidth.large}
            isSkeleton={isLoading}
            labelledBy='proposedDepth'
            value={`${fields.proposedDepth ?? ''}`}
          />
        </FormField>
        <FormField
          toolTipMessage={TOOLTIP.proposedMaxDiameter}
          isSkeleton={isLoading}
          labelId='proposedDiameter'
          label='Proposed diameter'
        >
          <ReadOnlyField
            fieldSize={InputWidth.large}
            isSkeleton={isLoading}
            labelledBy='proposedDiameter'
            value={`${fields.proposedDiameter ?? ''}`}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='drillingCompany' label='Drilling company'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='drillingCompany'
            value={`${fields.drillingCompany ?? ''}`}
          />
        </FormField>
        {(isDriller(user) || isCouncilUserOrAdmin(user)) && (
          <FormField isSkeleton={isLoading} labelId='drillersName' label='Drillers name'>
            <ReadOnlyField isSkeleton={isLoading} labelledBy='drillersName' value={`${fields.drillersName ?? ''}`} />
          </FormField>
        )}
        <FormField isSkeleton={isLoading} labelId='drillingMethod' label='Drilling method'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='drillingMethod' value={`${fields.drillingMethod ?? ''}`} />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='developmentMethod' label='Development method'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='developmentMethod'
            value={`${fields.developmentMethod ?? ''}`}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='drillingDate' label='Drilling date'>
          <ReadOnlyField
            fieldSize={InputWidth.large}
            isSkeleton={isLoading}
            labelledBy='drillingDate'
            value={`${fields.drillingDate ?? ''}`}
          />
        </FormField>
        <FormField
          toolTipMessage={TOOLTIP.maxDepthDrilled}
          isSkeleton={isLoading}
          labelId='drillingDepth'
          label='Drilling depth (m)'
        >
          <ReadOnlyField
            fieldSize={InputWidth.large}
            isSkeleton={isLoading}
            labelledBy='drillingDepth'
            value={`${fields.actualDepthDrilled ?? ''}`}
          />
        </FormField>
        {/** TODO: Updated maximum diameter value to be dynamic */}
        <FormField
          toolTipMessage={TOOLTIP.actualMaxDiameter}
          isSkeleton={isLoading}
          labelId='maxDiameter'
          label='Maximum diameter (mm)'
        >
          <ReadOnlyField
            fieldSize={InputWidth.large}
            isSkeleton={isLoading}
            labelledBy='maxDiameter'
            value={`${fields.actualMaxDiameter ?? ''}`}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='aboveMeasuringPoint' label='Water level'>
          <ReadOnlyField
            fieldSize={InputWidth.large}
            isSkeleton={isLoading}
            labelledBy='aboveMeasuringPoint'
            value={getAboveMeasuringPointDisplayValue()}
          />
        </FormField>

        <FormField isSkeleton={isLoading} labelId='staticWaterLevel' label='Static water level (m)'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='staticWaterLevel' value={fields.staticWaterLevel} />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='staticWaterLevelMeasuredDate' label='Static water level date'>
          <ReadOnlyField
            fieldSize={InputWidth.large}
            isSkeleton={isLoading}
            labelledBy='staticWaterLevelMeasuredDate'
            value={`${fields.staticWaterLevelMeasuredDate ?? ''}`}
          />
        </FormField>
      </RecordContent>
      <RecordFooter
        notes={
          Array.isArray(fields.notes) &&
          fields.notes.length > 0 && <NotesWrapperView notes={fields.notes} isLoading={isLoading} />
        }
        areActionsFixed={true}
      >
        <WellViewButtons wellId={id} />
      </RecordFooter>
    </>
  )
}
